<script setup>
// M26.1 — shared chrome (header + tab bar + content wrapper) for every
// Project workspace page. `standalone: false` renders only the slot, which
// is what lets ProjectDocuments/ProjectInventory keep being embedded inline
// inside ProjectForm's own tab switching with zero duplicated chrome.
import { ref, nextTick, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  // Whether to render the header + tab bar. ProjectDocuments/ProjectInventory
  // pass false when embedded inside ProjectForm's own tab switching, so only
  // one copy of the chrome ever exists on screen at a time.
  showChrome: { type: Boolean, default: true },
  project: { type: Object, default: null },
  tabs: { type: Array, required: true },
  activeTabKey: { type: String, required: true },
  backTo: { type: Object, default: null },
  icon: { type: String, default: 'i-lucide-building-2' },
  title: { type: String, default: null },
});

const emit = defineEmits(['tabClick']);

const router = useRouter();

const STATUS_META = {
  under_construction: {
    label: 'Under Construction',
    class:
      'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-800/20 dark:text-amber-400',
  },
  ready_to_move: {
    label: 'Ready to Move',
    class:
      'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400',
  },
  sold: {
    label: 'Sold',
    class: 'bg-n-alpha-2 text-n-slate-8 border-n-weak',
  },
};

function back() {
  router.push(props.backTo || { name: 'nexceo_projects_index' });
}

function handleTabClick(tab) {
  if (tab.to) {
    router.push(tab.to);
  } else {
    emit('tabClick', tab.key);
  }
}

// ── Sliding active-tab indicator ────────────────────────────────────────
const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

function setTabRef(el, index) {
  tabRefs.value[index] = el;
}

function activeIndex() {
  return props.tabs.findIndex(t => t.key === props.activeTabKey);
}

function updateIndicator() {
  nextTick(() => {
    const el = tabRefs.value[activeIndex()];
    if (!el) return;
    indicatorStyle.value = {
      left: `${el.offsetLeft}px`,
      width: `${el.offsetWidth}px`,
    };
  });
}

watch(() => props.activeTabKey, updateIndicator);
watch(() => props.tabs, updateIndicator);
onMounted(() => {
  updateIndicator();
  nextTick(() => {
    enableTransition.value = true;
  });
});
useResizeObserver(
  () => tabRefs.value[activeIndex()],
  () => updateIndicator()
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <template v-if="showChrome">
      <!-- Header -->
      <div
        class="flex items-center gap-3 px-4 h-14 border-b border-n-weak bg-n-background shrink-0"
      >
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors shrink-0"
          title="Back to Projects"
          @click="back"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <span class="size-4 text-woot-500 shrink-0" :class="icon" />
        <template v-if="title || project">
          <h1 class="text-base font-semibold text-n-slate-12 truncate">
            {{ title || project.name }}
          </h1>
          <span
            v-if="project?.status && STATUS_META[project.status]"
            class="text-xs font-medium px-2 py-0.5 rounded-full border capitalize shrink-0"
            :class="STATUS_META[project.status].class"
          >
            {{ STATUS_META[project.status].label }}
          </span>
        </template>
        <div
          v-else
          class="h-4 w-40 rounded bg-n-alpha-2 animate-pulse"
          aria-hidden="true"
        />
        <div class="flex-1" />
        <div class="flex items-center gap-2 shrink-0">
          <slot name="actions" />
        </div>
      </div>

      <!-- Tab bar -->
      <div
        v-if="tabs.length"
        class="relative flex items-center gap-0 px-4 border-b border-n-weak bg-n-background shrink-0 overflow-x-auto"
      >
        <button
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :ref="el => setTabRef(el, index)"
          type="button"
          class="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
          :class="
            tab.key === activeTabKey
              ? 'text-woot-600 dark:text-woot-400'
              : 'text-n-slate-9 hover:text-n-slate-12'
          "
          @click="handleTabClick(tab)"
        >
          <span class="size-3.5" :class="tab.icon" />
          {{ tab.label }}
        </button>
        <span
          class="absolute bottom-0 h-0.5 bg-woot-500 pointer-events-none"
          :class="{ 'transition-all duration-300 ease-out': enableTransition }"
          :style="indicatorStyle"
        />
      </div>
    </template>

    <!-- Content -->
    <div class="flex-1 min-h-0 overflow-hidden flex flex-col">
      <Transition mode="out-in" enter-active-class="animate-fade-in-up">
        <div
          :key="activeTabKey"
          class="flex-1 min-h-0 flex flex-col overflow-hidden"
        >
          <slot />
        </div>
      </Transition>
    </div>
  </div>
</template>
